import React, { useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Info from '../../SvgIcons/Info';
import { Tooltip } from '../../Tooltip';
import { ClearCircleIcon } from '../../CircleIcon';
import { Error } from '../Error';
import '../formV2.css';

const RadioField = (props) => {
  const {
    id,
    label,
    labelElem,
    className,
    useCompletedStyle,
    inline,
    disabled,
    tooltip,
    errorMessage,
    isExpanded,
    children,
    checked,
    useSelectorStyle,
    dek,
    rightSideZolaIconPath,
    rightSideText,
    rightSideHtml,
    ...rest
  } = props;
  const shownIcon = rightSideZolaIconPath ? (
    <div className="icon-wrapper">
      <ClearCircleIcon image={rightSideZolaIconPath} size={36} />
    </div>
  ) : null;

  const [isVisibleTooltip, setIsVisibleTooltip] = useState(false);
  const showTooltip = () => {
    setIsVisibleTooltip(true);
  };

  const hideTooltip = () => {
    setIsVisibleTooltip(false);
  };
  const tooltipProp = tooltip ? { 'data-tooltip': tooltip } : {};
  const componentClassName = classnames(
    'zola-ui v2-render-radio-field v2-render-selector-field',
    className,
    {
      inline,
      'has-error': errorMessage,
      'use-completed': useCompletedStyle,
      'uses-selector-style': useSelectorStyle || !!children,
      'is-checked': checked,
      'is-disabled': disabled,
      'is-expandable': children,
    }
  );
  const iconWrapperClassName = classnames('selector-icon-wrapper radio-icon-wrapper', {
    'selector-icon-wrapper--checked': checked,
    'selector-icon-wrapper--disabled': disabled,
    'selector-icon-wrapper--errored': errorMessage,
  });
  const selectorLabelClassName = classnames('selector-label', {
    'selector-label--checked': checked,
    'selector-label--disabled': disabled,
    'selector-label--errored': errorMessage,
  });
  return (
    <div className={componentClassName} {...tooltipProp}>
      <label htmlFor={id} className="field-wrapper">
        <input {...rest} type="radio" id={id} disabled={disabled} checked={checked} />
        <div className="field-wrapper-inner">
          <div className={iconWrapperClassName} />
          <div className="selector-label-wrapper">
            <span className={selectorLabelClassName}>
              {label && label}
              {tooltip && (
                <span className="label__tooltip">
                  <Info
                    className="form-field__icon info"
                    width={14}
                    height={14}
                    title="info-icon"
                    onMouseEnter={showTooltip}
                    onMouseLeave={hideTooltip}
                  />
                  {isVisibleTooltip && <Tooltip text={tooltip} />}
                </span>
              )}
              {labelElem && labelElem}
              {tooltip && <i className="zolaicon zolaicon-info" aria-label="Information Icon" />}
              {dek && <p className="selector-dek">{dek}</p>}
            </span>
            {(rightSideText || rightSideZolaIconPath || rightSideHtml) && (
              <span className="selector-label-right">
                {rightSideText && rightSideText}
                {shownIcon && shownIcon}
                {rightSideHtml && rightSideHtml}
              </span>
            )}
          </div>
        </div>
        {errorMessage && <Error message={errorMessage} iconSize={11} />}
      </label>
      {isExpanded && children && <div className="expanded-area">{children}</div>}
    </div>
  );
};

RadioField.propTypes = {
  /** mandatory id for label matching */
  id: PropTypes.string.isRequired,
  /** make a checkbox inline-block */
  inline: PropTypes.bool,
  /** label for the checkbox button */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** React Element as a label for the checkbox button */
  labelElem: PropTypes.element,
  /** custom function that triggered by onChange event of the input */
  onChange: PropTypes.func,
  /** disable default action for onChange event of the input */
  disableDefaultAction: PropTypes.bool,
  /** custom class name */
  className: PropTypes.string,
  /** use completed style instead of regular select style */
  useCompletedStyle: PropTypes.bool,
  /** disable the checkbox button */
  disabled: PropTypes.bool,
  /** NOTE: it still uses bootstrap to handle tooltip at the moment */
  tooltip: PropTypes.string,
  /** The text of any error message */
  errorMessage: PropTypes.string,
  /** Whether or not the checkbox should show its children. Only used for checkboxes which should be expandable. */
  isExpanded: PropTypes.bool,
  /** The contents that are shown when a checkbox is expanded */
  children: PropTypes.element,
  /** Whether or not the checkbox is checked / === true  */
  checked: PropTypes.bool,
  /** Whether or not the checkbox uses the selector style, i.e. the big, bordered version */
  useSelectorStyle: PropTypes.bool,
  /** The text that appears below the label */
  dek: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** The path to the zola icon that can appear to the right of the label */
  rightSideZolaIconPath: PropTypes.string,
  /** Text that can appear to the right of the label, bolded */
  rightSideText: PropTypes.string,
  /** Typically an <img> or <svg> tag. Should be 36px tall */
  rightSideHtml: PropTypes.node,
};

export default RadioField;
